<script setup>
import CtaBanner from '@/views/front-pages/landing-page/CtaBanner.vue'
import PageHero from '@/views/front-pages/PageHero.vue'
import PublicPageShell from '@/views/front-pages/PublicPageShell.vue'
import { useApi } from '@/utils/api'
import { useConfigStore } from '@core/stores/config'

definePage({ meta: { layout: 'blank', public: true } })

const store = useConfigStore()

store.skin = 'default'

// Addendum 15, Modul BO: KETERSEDIAAN tiap fitur (badge Premium/Addon/Base)
// diambil live dari GET /api/public/plans (feature_flags per paket) --
// SAMA endpoint yang sudah dipakai PricingSection.vue/FeaturesSection.vue
// di Beranda, BUKAN daftar hardcode baru. Yang statis di bawah cuma
// pengelompokan kategori & teks deskripsi (copywriting), sama filosofi
// dengan file-file itu.
const plans = ref([])
const plansLoading = ref(true)
const loadError = ref(false)

const fetchPlans = async () => {
  try {
    const response = await useApi('/api/public/plans').get()
    const body = response?.data ?? response

    plans.value = Array.isArray(body) ? body : (body?.data ?? [])
  } catch (err) {
    console.error('❌ Gagal memuat data paket:', err)
    loadError.value = true
  } finally {
    plansLoading.value = false
  }
}

onMounted(fetchPlans)

const findPlan = slug => plans.value.find(p => p.slug === slug) ?? null
const planFlag = (slug, flag) => findPlan(slug)?.feature_flags?.[flag]

// feature_flags berisi boolean, string tier ('dasar'/'lengkap'/'custom'),
// atau angka (multi_device_sync) -- "aktif" berarti bukan false/null/undefined.
const isEnabled = value => value !== undefined && value !== null && value !== false

// Patch: "detailkan" halaman Fitur -- tiap item sekarang punya tagline +
// daftar kemampuan konkret (capabilities) + 1 kalimat manfaat (benefit),
// bukan cuma 1 kalimat ringkas seperti sebelumnya. Semua kemampuan yang
// ditulis di bawah adalah fitur yang BENAR-BENAR ada & sudah dibangun di
// project ini (Addendum 01-14) -- tidak ada yang ditambahkan/dikarang.
// Kategori & flag SAMA PERSIS dengan sebelumnya (lihat
// comparisonRowTemplates di PricingSection.vue sebagai referensi silang),
// modul ini hanya memperkaya kontennya.
const CATEGORIES = [
  {
    title: 'Operasional Kasir',
    icon: 'ri-computer-line',
    items: [
      {
        name: 'POS Kasir Sentuh Cepat',
        flag: 'pos_kasir',
        tagline: 'Interface kasir fullscreen yang responsif, dirancang untuk kecepatan kerja kasir sehari-hari.',
        capabilities: [
          'Layar sentuh fullscreen yang dioptimalkan untuk transaksi cepat',
          'Keyboard shortcut untuk input tanpa perlu mouse',
          'Cetak struk thermal & kirim struk digital otomatis ke WhatsApp/Email pelanggan',
        ],
        benefit: 'Antrian di kasir terurai lebih cepat, terutama saat jam sibuk.',
      },
      {
        name: 'Shift Management',
        flag: 'shift_management',
        tagline: 'Buka dan tutup kasir per shift dengan rekonsiliasi kas otomatis.',
        capabilities: [
          'Catat modal awal dan setoran akhir tiap shift',
          'Rekonsiliasi otomatis antara kas fisik dan catatan sistem',
          'Riwayat shift per kasir untuk kebutuhan audit',
        ],
        benefit: 'Selisih kas ketahuan sejak akhir shift, bukan menumpuk sampai akhir bulan.',
      },
      {
        name: 'Manajemen Menu, Kategori & Varian',
        flag: 'menu_management',
        tagline: 'Atur produk, kategori, varian, dan modifier/topping dengan mudah.',
        capabilities: [
          'Kelola kategori dan produk tanpa batas jumlah',
          'Varian per produk (ukuran, rasa, dll) dengan harga masing-masing',
          'Modifier/topping tambahan yang bisa dipilih pelanggan saat pesan',
        ],
        benefit: 'Menu selalu mengikuti perubahan bisnis, tanpa perlu bantuan developer.',
      },
      {
        name: 'Struk Digital via WhatsApp & Email',
        flag: 'struk_whatsapp_email',
        tagline: 'Kirim struk otomatis ke pelanggan tanpa perlu cetak kertas.',
        capabilities: [
          'Kirim struk digital langsung ke WhatsApp atau Email pelanggan',
          'Bisa dikombinasikan dengan cetak struk fisik kalau perlu',
          'Format struk rapi lengkap dengan rincian item & pembayaran',
        ],
        benefit: 'Hemat kertas, dan pelanggan punya bukti transaksi digital yang mudah dicari lagi.',
      },
      {
        name: 'Table Management & Split Bill',
        flag: 'table_management',
        tagline: 'Kelola meja dine-in dan pisahkan tagihan dalam satu transaksi.',
        capabilities: [
          'Peta meja visual dengan status kosong/terisi',
          'Pisah tagihan (split bill) per item atau merata ke beberapa orang',
          'Gabung beberapa meja untuk rombongan besar',
        ],
        benefit: 'Cocok untuk resto/kafe dine-in yang butuh manajemen meja yang rapi.',
      },
      {
        name: 'Order Type (Dine-in/Takeaway/Delivery)',
        flag: 'order_type_delivery',
        tagline: 'Tandai tipe pesanan, termasuk pencatatan order dari platform GoFood/GrabFood.',
        capabilities: [
          'Pilih tipe order: Dine-in, Takeaway, atau Delivery',
          'Catat order dari GoFood/GrabFood secara manual untuk rekonsiliasi',
          'Laporan penjualan bisa dipecah per tipe order',
        ],
        benefit: 'Owner tahu persis kontribusi tiap channel penjualan, dine-in maupun online delivery.',
      },
    ],
  },
  {
    title: 'Self-Order & Dapur',
    icon: 'ri-restaurant-line',
    items: [
      {
        name: 'Self-Order QR Pelanggan',
        flag: 'self_order',
        tagline: 'Pelanggan pesan sendiri lewat scan QR di meja, langsung masuk antrian dapur.',
        capabilities: [
          'Scan QR unik per meja untuk mulai memesan',
          'Menu lengkap dengan modifier/topping seperti di kasir',
          'Order otomatis masuk ke antrian dapur tanpa input ulang oleh kasir',
        ],
        benefit: 'Mengurangi beban kasir saat jam ramai, pelanggan bisa pesan tanpa menunggu dilayani.',
      },
      {
        name: 'Kitchen Display System (KDS)',
        flag: 'kds',
        tagline: 'Layar dapur multi-stasiun menggantikan bon kertas, real-time saat ada order baru.',
        capabilities: [
          'Tampilan antrian order real-time per stasiun (mis. bar, dapur panas)',
          'Tandai status item: diproses, selesai, diambil',
          'Notifikasi otomatis begitu order baru masuk',
        ],
        benefit: 'Dapur tidak lagi kehilangan bon kertas, alur kerja lebih terorganisir saat ramai.',
      },
      {
        name: 'Web Bluetooth Printer Engine',
        flag: 'printer_bluetooth',
        tagline: 'Cetak struk & bon dapur langsung dari browser ke printer thermal Bluetooth.',
        capabilities: [
          'Sambungkan printer thermal Bluetooth langsung dari browser, tanpa driver tambahan',
          'Cetak struk kasir dan bon dapur secara terpisah',
          'Mendukung multi-printer untuk kebutuhan berbeda (kasir vs dapur)',
        ],
        benefit: 'Tidak perlu komputer atau software tambahan hanya untuk mencetak.',
      },
      {
        name: 'Customer Display System',
        flag: 'customer_display',
        tagline: 'Layar kedua untuk pelanggan yang menampilkan rincian belanja dan kembalian secara real-time.',
        capabilities: [
          'Pairing sekali klik dari layar kasir ke layar pelanggan',
          'Tampilkan rincian item, promo/voucher/poin yang dipakai, dan kembalian',
          'Transparansi transaksi langsung terlihat oleh pelanggan',
        ],
        benefit: 'Pelanggan lebih percaya karena bisa melihat sendiri rincian belanjanya.',
      },
      {
        name: 'Qwen Voice AI Agent',
        flag: 'voice_ai',
        tagline: 'Input pesanan lewat suara -- AI membaca varian, kustomisasi, dan topping otomatis.',
        capabilities: [
          'Kasir cukup berbicara, AI otomatis mendeteksi produk, varian, dan topping',
          'Order langsung masuk ke keranjang tanpa klik manual satu per satu',
          'Mempercepat input pesanan dengan banyak kustomisasi',
        ],
        benefit: 'Proses input pesanan jauh lebih cepat dibanding mengetik/klik satu per satu.',
      },
    ],
  },
  {
    title: 'Inventory & Pembelian',
    icon: 'ri-archive-stack-line',
    items: [
      {
        name: 'Inventory Engine (BOM/Resep)',
        flag: 'inventory_bom',
        tagline: 'Stok bahan baku otomatis terkurangi sesuai resep saat ada transaksi, HPP dihitung otomatis.',
        capabilities: [
          'Definisikan resep (Bill of Materials) per produk/varian',
          'Stok bahan baku otomatis berkurang tiap transaksi sesuai resep',
          'HPP dihitung dan di-snapshot otomatis pada saat penjualan',
        ],
        benefit: 'Owner tahu persis margin tiap produk, tanpa perlu hitung manual.',
      },
      {
        name: 'Purchase Order & Supplier Management',
        flag: 'purchase_order',
        tagline: 'Catat pemesanan ke supplier hingga penerimaan barang, stok bertambah otomatis saat diterima.',
        capabilities: [
          'Kelola data supplier dan riwayat pembelian',
          'Buat Purchase Order, kirim ke supplier, lalu konfirmasi penerimaan barang',
          'Kalkulator HPP otomatis dari harga beli, termasuk satuan pembelian berbeda (mis. beli per Kg, stok tercatat per gram)',
        ],
        benefit: 'Stok bahan baku selalu akurat, dan histori pembelian per supplier tercatat rapi.',
      },
      {
        name: 'Stock Opname & Adjustment',
        flag: 'stock_opname',
        tagline: 'Hitung stok fisik berkala dan sesuaikan selisihnya dengan catatan yang tercatat rapi.',
        capabilities: [
          'Sesi stock opname terjadwal maupun on-demand',
          'Catat selisih stok fisik vs sistem lengkap dengan alasan',
          'Riwayat penyesuaian stok tersimpan untuk kebutuhan audit',
        ],
        benefit: 'Penyusutan atau selisih stok terdeteksi dan terdokumentasi, bukan sekadar dikoreksi diam-diam.',
      },
    ],
  },
  {
    title: 'Keuangan & Laporan',
    icon: 'ri-bar-chart-2-line',
    items: [
      {
        name: 'Laporan Penjualan',
        flag: 'laporan_penjualan',
        tier: true,
        tagline: 'Pantau omzet, produk terlaris, dan grafik penjualan -- kedalaman laporan meningkat sesuai paket.',
        capabilities: [
          'Laporan omzet harian maupun bulanan secara real-time',
          'Produk terlaris dan tren penjualan',
          'Paket lebih tinggi membuka laporan yang lebih mendalam dan custom',
        ],
        benefit: 'Keputusan bisnis (restock, promosi, dll) berdasarkan data, bukan perkiraan.',
      },
      {
        name: 'Pencatatan Beban Biaya (Expense)',
        flag: 'expense_tracking',
        tagline: 'Catat biaya operasional (sewa, listrik, gaji, dll) di luar pembelian bahan baku.',
        capabilities: [
          'Kategori beban biaya bisa disesuaikan dengan kebutuhan bisnis',
          'Catat pengeluaran rutin maupun insidentil',
          'Terhubung langsung ke laporan laba rugi otomatis',
        ],
        benefit: 'Semua biaya operasional tercatat rapi, tidak tercecer di catatan terpisah.',
      },
      {
        name: 'Laporan Laba Rugi Otomatis',
        flag: 'laporan_laba_rugi',
        tagline: 'Laba bersih dihitung otomatis dari omzet dikurangi HPP dan beban operasional.',
        capabilities: [
          'Laporan laba rugi real-time tanpa perlu hitung manual',
          'Menggabungkan data penjualan, HPP, dan beban operasional',
          'Bisa dilihat per periode (harian/bulanan)',
        ],
        benefit: 'Owner tahu untung-rugi bisnis kapan saja, tanpa menunggu tutup buku akhir bulan.',
      },
      {
        name: 'Custom Report & Export PDF/Excel',
        flag: 'custom_report_export',
        tagline: 'Export laporan ke format PDF/Excel untuk kebutuhan pembukuan atau presentasi.',
        capabilities: [
          'Export laporan penjualan, inventory, dan laba rugi',
          'Format PDF untuk presentasi, Excel untuk olah data lanjutan',
          'Bisa dipakai untuk keperluan pajak atau akuntan',
        ],
        benefit: 'Data siap pakai untuk kebutuhan eksternal (akuntan, investor, pajak) tanpa input ulang.',
      },
      {
        name: 'Integrasi Payment Gateway',
        flag: 'payment_gateway',
        tagline: 'Terima QRIS dinamis, virtual account, debit, dan kartu kredit -- tercatat otomatis tanpa rekonsiliasi manual.',
        capabilities: [
          'QRIS dinamis otomatis mengikuti nominal transaksi',
          'Virtual account bank transfer',
          'Pembayaran debit dan kartu kredit',
        ],
        benefit: 'Tidak perlu rekonsiliasi manual antara catatan kasir dan mutasi bank/e-wallet.',
      },
    ],
  },
  {
    title: 'Promo, Loyalty & Marketing',
    icon: 'ri-price-tag-3-line',
    items: [
      {
        name: 'Promo, Diskon & Voucher',
        flag: 'promo_voucher',
        tagline: 'Buat promo otomatis dan kode voucher, bisa digabung dalam satu transaksi.',
        capabilities: [
          'Promo otomatis berdasarkan syarat (mis. minimum belanja, produk tertentu)',
          'Kode voucher yang bisa dibagikan ke pelanggan',
          'Bisa dikombinasikan dalam satu transaksi sesuai aturan yang berlaku',
        ],
        benefit: 'Strategi promosi jadi fleksibel tanpa diskon manual yang rawan human error.',
      },
      {
        name: 'Program Loyalty (Poin/Stempel)',
        loyalty: true,
        tagline: 'Poin atau stempel pelanggan untuk retensi -- kombinasi Poin + Stempel sekaligus khusus paket Premium.',
        capabilities: [
          'Mode Poin: pelanggan kumpulkan poin tiap transaksi untuk ditukar reward',
          'Mode Stempel: kumpulkan stempel digital, mis. beli 10 gratis 1',
          'Paket Premium bisa menggabungkan kedua mode sekaligus',
        ],
        benefit: 'Pelanggan lebih sering kembali karena ada insentif nyata, bukan cuma promo sesaat.',
      },
      {
        name: 'CRM & Broadcast Marketing',
        flag: 'crm_broadcast',
        tagline: 'Segmentasi pelanggan dan kirim broadcast promo via WhatsApp/Email langsung dari dashboard.',
        capabilities: [
          'Segmentasi otomatis (mis. "tidak belanja 30 hari", "pelanggan VIP")',
          'Template pesan promosi yang bisa disesuaikan',
          'Broadcast massal via WhatsApp atau Email langsung dari dashboard',
        ],
        benefit: 'Reaktivasi pelanggan lama dan promosi tertarget tanpa perlu tools marketing terpisah.',
      },
    ],
  },
  {
    title: 'Refund & Otorisasi',
    icon: 'ri-refund-2-line',
    items: [
      {
        name: 'Refund Berotorisasi',
        always: true,
        tagline: 'Refund sebagian/seluruh item dengan alasan tercatat dan otorisasi PIN Manager/Owner.',
        capabilities: [
          'Refund sebagian item atau seluruh transaksi',
          'Wajib mengisi alasan refund yang tercatat untuk audit',
          'Otorisasi PIN Manager/Owner sebelum refund diproses, dengan opsi restock bahan baku otomatis',
        ],
        benefit: 'Refund tidak bisa dilakukan sembarangan oleh kasir, mengurangi risiko kecurangan.',
      },
    ],
  },
  {
    title: 'Skalabilitas & Konektivitas',
    icon: 'ri-device-line',
    items: [
      {
        name: 'Multi-Device Sync',
        flag: 'multi_device_sync',
        tagline: 'Beberapa perangkat kasir sinkron real-time dalam satu outlet.',
        capabilities: [
          'Beberapa kasir/perangkat bisa beroperasi bersamaan',
          'Data stok dan transaksi sinkron real-time antar perangkat',
          'Jumlah device mengikuti paket (terbatas di Pro, unlimited di Premium)',
        ],
        benefit: 'Outlet ramai dengan banyak kasir tetap konsisten datanya, tidak ada data bentrok.',
      },
      {
        name: 'Realtime Broadcast',
        flag: 'realtime_broadcast',
        tagline: 'Update status order/KDS/Customer Display terkirim instan ke semua layar terkait.',
        capabilities: [
          'Update status order real-time ke semua layar terkait (KDS, Customer Display, dashboard)',
          'Berbasis teknologi broadcast (Pusher/Echo)',
          'Tidak perlu refresh manual untuk melihat perubahan',
        ],
        benefit: 'Semua pihak (kasir, dapur, pelanggan) melihat status yang sama tanpa delay.',
      },
    ],
  },
  {
    title: 'Dukungan & Enterprise',
    icon: 'ri-customer-service-2-line',
    items: [
      {
        name: 'Priority Support 24/7',
        flag: 'priority_support',
        tagline: 'Jalur bantuan prioritas kapan saja dibutuhkan.',
        capabilities: [
          'Respons lebih cepat dibanding jalur support standar',
          'Tersedia 24/7 untuk kebutuhan operasional yang mendesak',
          'Cocok untuk bisnis yang beroperasi di luar jam kerja normal',
        ],
        benefit: 'Kendala operasional teratasi cepat, minim downtime yang mengganggu penjualan.',
      },
      {
        name: 'White-Label Option',
        flag: 'white_label',
        tagline: 'Branding aplikasi dengan identitas bisnis Anda sendiri.',
        capabilities: [
          'Ganti logo dan identitas visual sesuai brand bisnis',
          'Tampilan self-order/struk digital mengikuti branding sendiri',
          'Cocok untuk bisnis dengan beberapa outlet (tiap outlet daftar akun terpisah) yang ingin konsistensi brand',
        ],
        benefit: 'Pengalaman pelanggan terasa seperti aplikasi milik bisnis sendiri.',
      },
      {
        name: 'Dedicated Account Manager',
        flag: 'dedicated_account_manager',
        tagline: 'Pendamping khusus untuk kebutuhan onboarding dan operasional.',
        capabilities: [
          'Satu kontak dedicated untuk pertanyaan dan kebutuhan khusus',
          'Bantuan onboarding lebih personal saat migrasi/setup awal',
          'Konsultasi kebutuhan operasional sesuai skala bisnis',
        ],
        benefit: 'Tidak perlu bolak-balik menjelaskan konteks bisnis ke support yang berbeda-beda.',
      },
    ],
  },
]

const badgeFor = item => {
  if (item.addon) return 'Addon'
  if (item.always || item.loyalty) return 'Base'

  const starterOn = isEnabled(planFlag('starter', item.flag))
  const proOn = isEnabled(planFlag('pro', item.flag))
  const premiumOn = isEnabled(planFlag('premium', item.flag))

  // Premium eksklusif: mati di Starter & Pro, hanya hidup di Premium
  // (Addendum 12 §0 -- Promo/Voucher, CRM/Broadcast, Customer Display).
  if (premiumOn && !starterOn && !proOn) return 'Premium'

  return 'Base'
}

const BADGE_COLOR = { Base: 'default', Premium: 'info', Addon: 'warning' }

const categories = computed(() => CATEGORIES.map(category => ({
  ...category,
  items: category.items.map(item => ({ ...item, badge: badgeFor(item) })),
})))

// Absensi Karyawan BUKAN bagian dari plans.feature_flags -- dia addon
// berbayar terpisah (Modul AY/AZ), belum ada endpoint publik untuk katalog
// addon (lihat komentar ADDON_ABSENSI di PricingSection.vue). Angka di
// bawah SENGAJA disamakan persis dengan yang sudah dikonfirmasi & dipakai
// di PricingSection.vue, bukan angka baru.
const addon = {
  name: 'Absensi Karyawan',
  tagline: 'Staff absen lewat PIN/QR di kiosk toko, lengkap validasi lokasi dan foto verifikasi.',
  capabilities: [
    'Absen via PIN atau QR code langsung dari kiosk toko',
    'Validasi lokasi (geofence) memastikan staff absen di lokasi toko',
    'Foto verifikasi opsional saat absen, laporan kehadiran siap dipakai untuk penggajian',
  ],
  benefit: 'Bisa dibeli dan diaktifkan di paket manapun tanpa perlu upgrade paket.',
}
</script>

<template>
  <PublicPageShell>
    <PageHero
      eyebrow="Fitur Lengkap"
      eyebrow-icon="ri-apps-2-line"
      title="Semua Fitur PintuKasir, Tanpa Terkecuali"
      subtitle="Dari operasional kasir harian sampai promo, loyalty, dan laporan keuangan -- jelajahi seluruh kemampuan platform, dikelompokkan per kategori."
    />

    <section class="features-detail-section pb-16">
      <VContainer>
        <VAlert
          v-if="loadError"
          type="warning"
          variant="tonal"
          class="mx-auto mb-10 feature-alert"
        >
          Gagal memuat status ketersediaan fitur per paket. Badge di bawah mungkin belum akurat -- silakan muat ulang halaman.
        </VAlert>

        <!--
          Badge Premium/Addon/Base butuh feature_flags dari API lebih
          dulu -- tampilkan loading dulu supaya tidak sempat kelihatan
          badge "Base" yang keliru sesaat sebelum data live selesai dimuat.
        -->
        <div
          v-if="plansLoading"
          class="text-center py-12"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </div>

        <template v-else>
          <div
            v-for="category in categories"
            :key="category.title"
            class="feature-category mb-14"
          >
            <div class="d-flex align-center gap-2 mb-6">
              <VIcon
                :icon="category.icon"
                size="22"
                color="primary"
              />
              <h2 class="text-h5 font-weight-bold mb-0">
                {{ category.title }}
              </h2>
            </div>

            <VRow>
              <VCol
                v-for="item in category.items"
                :key="item.name"
                cols="12"
                sm="6"
                lg="4"
              >
                <VCard
                  class="feature-detail-card h-100"
                  rounded="lg"
                  variant="outlined"
                >
                  <VCardText class="pa-5 d-flex flex-column h-100">
                    <div class="d-flex align-start justify-space-between mb-2 gap-2">
                      <h3 class="text-body-1 font-weight-bold mb-0">
                        {{ item.name }}
                      </h3>
                      <VChip
                        size="x-small"
                        :color="BADGE_COLOR[item.badge]"
                        :variant="item.badge === 'Base' ? 'tonal' : 'elevated'"
                        class="font-weight-bold flex-shrink-0"
                      >
                        {{ item.badge }}
                      </VChip>
                    </div>

                    <p class="text-body-2 text-medium-emphasis mb-3" style="line-height: 1.6">
                      {{ item.tagline }}
                    </p>

                    <ul class="feature-capabilities mb-3">
                      <li
                        v-for="capability in item.capabilities"
                        :key="capability"
                      >
                        {{ capability }}
                      </li>
                    </ul>

                    <div class="feature-benefit mt-auto pt-3">
                      <VIcon icon="ri-lightbulb-flash-line" size="16" color="warning" class="me-1" />
                      <span class="text-caption font-weight-medium">{{ item.benefit }}</span>
                    </div>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </div>

          <!-- Addon Terpisah -->
          <div class="feature-category">
            <div class="d-flex align-center gap-2 mb-6">
              <VIcon
                icon="ri-puzzle-line"
                size="22"
                color="primary"
              />
              <h2 class="text-h5 font-weight-bold mb-0">
                Addon Berbayar Terpisah
              </h2>
            </div>
            <VRow>
              <VCol
                cols="12"
                sm="6"
                lg="4"
              >
                <VCard
                  class="feature-detail-card h-100"
                  rounded="lg"
                  variant="outlined"
                >
                  <VCardText class="pa-5 d-flex flex-column h-100">
                    <div class="d-flex align-start justify-space-between mb-2 gap-2">
                      <h3 class="text-body-1 font-weight-bold mb-0">
                        {{ addon.name }}
                      </h3>
                      <VChip size="x-small" color="warning" variant="elevated" class="font-weight-bold flex-shrink-0">
                        Addon
                      </VChip>
                    </div>

                    <p class="text-body-2 text-medium-emphasis mb-3" style="line-height: 1.6">
                      {{ addon.tagline }}
                    </p>

                    <ul class="feature-capabilities mb-3">
                      <li
                        v-for="capability in addon.capabilities"
                        :key="capability"
                      >
                        {{ capability }}
                      </li>
                    </ul>

                    <div class="feature-benefit mt-auto pt-3">
                      <VIcon icon="ri-lightbulb-flash-line" size="16" color="warning" class="me-1" />
                      <span class="text-caption font-weight-medium">{{ addon.benefit }}</span>
                    </div>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </div>
        </template>
      </VContainer>
    </section>

    <CtaBanner />
  </PublicPageShell>
</template>

<style lang="scss" scoped>
.features-detail-section {
  background: rgba(var(--v-theme-surface), 1);
}

.feature-alert {
  max-inline-size: 720px;
}

.feature-detail-card {
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 8px 24px rgba(var(--v-shadow-key-umbra-color), 12%) !important;
    transform: translateY(-2px);
  }
}

.feature-capabilities {
  margin: 0;
  padding-inline-start: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;

  li {
    font-size: 0.8125rem;
    line-height: 1.5;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }
}

.feature-benefit {
  border-block-start: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}
</style>
